import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { Sparkles, ArrowLeft, Loader2 } from "lucide-react";

export function ReadingProgress() {
  const [w, setW] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      setW(max > 0 ? (h.scrollTop / max) * 100 : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return <div className="reading-progress" style={{ width: `${w}%` }} data-testid="reading-progress" />;
}

export function getEmbed(url) {
  if (!url) return null;
  const u = url.trim();
  let m = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
  if (m) return { type: "iframe", src: `https://www.youtube.com/embed/${m[1]}` };
  m = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (m) return { type: "iframe", src: `https://player.vimeo.com/video/${m[1]}` };
  if (/\.(mp4|webm|ogg)(\?.*)?$/i.test(u)) return { type: "video", src: u };
  return { type: "iframe", src: u };
}

export function PostHeader({ post }) {
  return (
    <article className="max-w-3xl mx-auto px-6 sm:px-10">
      <Link to="/blog" data-testid="back-to-blog" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-[var(--brand-muted)] hover:text-[var(--brand-primary)] transition-colors mb-10">
        <ArrowLeft size={16} /> All insights
      </Link>
      <p className="font-mono-brand text-xs uppercase tracking-[0.18em] text-[var(--brand-primary)]">
        {post.category} · {post.read_minutes} min read
      </p>
      <h1 data-testid="post-title" className="font-display text-5xl sm:text-7xl leading-[0.95] tracking-tight mt-4 t-heading">
        {post.title}
      </h1>
      <p className="mt-6 text-xl text-[var(--brand-muted)] leading-relaxed font-medium t-body">{post.excerpt}</p>
      <div className="mt-8 flex items-center gap-3 text-sm text-[var(--brand-muted)]">
        <span className="font-bold text-[var(--brand-heading)]">{post.author_name}</span>
        <span>·</span>
        <span>{new Date(post.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</span>
      </div>
    </article>
  );
}

export function VideoEmbed({ url }) {
  if (!url) return null;
  const e = getEmbed(url);
  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-10 mb-12" data-testid="post-video">
      <div className="relative w-full rounded-[2rem] overflow-hidden shadow-[0_24px_50px_-20px_rgba(28,24,21,0.4)] bg-black" style={{ aspectRatio: "16 / 9" }}>
        {e.type === "video" ? (
          <video src={e.src} controls className="absolute inset-0 w-full h-full" />
        ) : (
          <iframe
            src={e.src}
            title="video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
            className="absolute inset-0 w-full h-full border-0"
          />
        )}
      </div>
    </div>
  );
}

export function AISummary({ summary, summarizing, onSummarize }) {
  return (
    <div data-testid="ai-summary-block" className="bg-[#FBF9F4] border border-[#E7DFD0] rounded-3xl shadow-[0_8px_30px_-14px_rgba(28,24,21,0.16)] p-7 mb-12">
      <div className="flex items-center gap-2 mb-4">
        <span className="inline-flex items-center gap-1.5 bg-[var(--brand-primary)] text-[var(--brand-cream)] text-xs font-bold uppercase tracking-[0.1em] px-3 py-1 rounded-full">
          <Sparkles size={13} /> AI Summary
        </span>
      </div>
      {summary ? (
        <p data-testid="ai-summary-text" className="text-[var(--brand-heading)] leading-relaxed font-medium">{summary}</p>
      ) : (
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
          <p className="text-[var(--brand-muted)] font-medium">Get the key takeaways in seconds.</p>
          <button
            data-testid="summarize-button"
            onClick={onSummarize}
            disabled={summarizing}
            className="inline-flex items-center gap-2 bg-[var(--brand-primary)] text-[var(--brand-cream)] px-5 py-3 rounded-full font-bold uppercase tracking-[0.1em] text-xs transition-all hover:bg-[var(--brand-primary-dark)] disabled:opacity-60 whitespace-nowrap"
          >
            {summarizing ? <><Loader2 size={16} className="animate-spin" /> Summarizing…</> : <><Sparkles size={16} /> Summarize with AI</>}
          </button>
        </div>
      )}
    </div>
  );
}

const isHtml = (s) => /<\/?[a-z][\s\S]*>/i.test(s || "");

function RichContent({ html }) {
  const ref = useRef(null);
  const clean = DOMPurify.sanitize(html, { ADD_ATTR: ["target", "rel"] });

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    root.querySelectorAll("pre").forEach((pre) => {
      if (pre.querySelector(".code-copy-btn")) return;
      pre.style.position = "relative";
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "code-copy-btn";
      btn.textContent = "Copy";
      btn.addEventListener("click", () => {
        const code = pre.querySelector("code")?.innerText ?? pre.innerText;
        navigator.clipboard.writeText(code).then(() => {
          btn.textContent = "Copied!";
          setTimeout(() => { btn.textContent = "Copy"; }, 1600);
        });
      });
      pre.appendChild(btn);
    });
  }, [clean]);

  return <div ref={ref} className="post-content" data-testid="post-content" dangerouslySetInnerHTML={{ __html: clean }} />;
}

export function PostBody({ post }) {
  return (
    <>
      {isHtml(post.content) ? (
        <RichContent html={post.content} />
      ) : (
        <div className="post-content" data-testid="post-content">
          {post.content.split("\n\n").map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>
      )}
      {post.tags?.length > 0 && (
        <div className="mt-12 flex flex-wrap gap-2">
          {post.tags.map((t) => (
            <span key={t} className="font-mono-brand text-xs px-3 py-1.5 rounded-full border border-[#E7DFD0] bg-[#FBF9F4] text-[var(--brand-heading)]">#{t}</span>
          ))}
        </div>
      )}
    </>
  );
}
